/**
The MIT License (MIT)

Copyright (c) Honeysol inc. (honeysol.com)

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
 */

import { getAPI } from "./GoogleMapAPI";

function radians(deg) {
  return (deg * Math.PI) / 180;
}

export function getDistance(loc1, loc2) {
  if (!loc1 || !loc2 || !loc1.lat || !loc1.lng || !loc2.lat || !loc2.lng) {
    return Number.POSITIVE_INFINITY;
  }
  return (
    6378.14 *
    Math.acos(
      Math.cos(radians(loc1.lat)) *
        Math.cos(radians(loc2.lat)) *
        Math.cos(radians(loc2.lng) - radians(loc1.lng)) +
        Math.sin(radians(loc1.lat)) * Math.sin(radians(loc2.lat))
    )
  );
}

export class BoundedMap {
  constructor(options) {
    this.target = options.target;
    this.markers = options.markers;
    this.targetElement = options.targetElement;
    this.getPosition = options.getPosition;
    this.getIcon = options.getIcon;
    this.getParam = options.getParam;
    this.infoWindow = options.infoWindow;
    this.onInfoWindowClicked = options.onInfoWindowClicked;
    this.zoom = options.zoom;
    this.init();
  }
  async init() {
    this.googleApiReady = getAPI();
    this.google = await this.googleApiReady;
    this.map = new this.google.maps.Map(this.targetElement, {
      gestureHandling: 'cooperative',
    });
    window.map = this.map;
    this.setMarkers(this.markers);
  }
  async setMarkers(markers) {
    this.google = await this.googleApiReady;
    this.markers = markers;
    if (!this.markers) {
      return;
    }
    if (this.currentMarkers) {
      this.currentMarkers.forEach(marker => {
        marker.setMap(null);
      });
    }
    const bounds = new this.google.maps.LatLngBounds();
    this.currentMarkers = this.markers.filter(markerParam => {
      const position = this.getPosition(markerParam);
      return position && position.lat && position.lng;
    }).map(markerParam => {
      const position = this.getPosition(markerParam);
      bounds.extend(position);
      const marker = new this.google.maps.Marker(Object.assign({
        icon: this.getIcon(markerParam),
        position: position,
        map: this.map,
      }, this.getParam && this.getParam(markerParam)));
      google.maps.event.addListener(marker, "click", () => {
        if (this.currentInfowindow) {
          this.currentInfowindow.close();
        }
        const infoWindowContent = document.createElement("div");

        infoWindowContent.innerHTML = this.infoWindow(markerParam);
        infoWindowContent.addEventListener("click", () => {
          if (this.onInfoWindowClicked) {
            this.onInfoWindowClicked(markerParam);
          }
        });
        this.currentInfowindow = new google.maps.InfoWindow({
          content: infoWindowContent,
        });
        this.currentInfowindow.open(this.map, marker);
      });

      return marker;
    });

    if (this.target) {
      const targetBounds = new this.google.maps.LatLngBounds();
      const position = this.getPosition(this.target);
      targetBounds.extend(position);

      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      const tj = bounds.getCenter().toJSON();
      const tbj = targetBounds.getCenter().toJSON();
      targetBounds.extend({ lat: (sw.lat() - (tj.lat - tbj.lat)), lng: (sw.lng() - (tj.lng - tbj.lng))});
      targetBounds.extend({ lat: (ne.lat() - (tj.lat - tbj.lat)), lng: (ne.lng() - (tj.lng - tbj.lng))});
      this.map.fitBounds(targetBounds);

      const { lat, lng } = targetBounds.getCenter().toJSON();
      const minLat = 0.01;
      const minLng = 0.01;
      targetBounds.extend({ lat: lat - minLat / 2, lng: lng - minLng / 2});
      targetBounds.extend({ lat: lat + minLat / 2, lng: lng + minLng / 2});
      this.map.fitBounds(targetBounds);
    } else {
      const { lat, lng } = bounds.getCenter().toJSON();
      const minLat = 0.01;
      const minLng = 0.01;
      bounds.extend({ lat: lat - minLat / 2, lng: lng - minLng / 2});
      bounds.extend({ lat: lat + minLat / 2, lng: lng + minLng / 2});
      this.map.fitBounds(bounds);
    }
  }
}



// WEBPACK FOOTER //
// ./lib/BoundedMap.js